import type { IconType } from "react-icons";
import { defineWizardSteps } from "../../../../common/wizard/wizardSteps";
import {
  FaAward,
  FaFileSignature,
  FaFileAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaShieldAlt,
  FaTasks,
  FaUserCheck,
  FaUserTie,
} from "react-icons/fa";
import type { PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";

export const BID_WIZARD_STEPS = [
  { key: "intro", icon: FaInfoCircle },
  { key: "supplier", icon: FaUserTie },
  { key: "exclusion", icon: FaUserCheck },
  { key: "qualification", icon: FaTasks },
  { key: "awardCriteria", icon: FaAward },
  { key: "minimumRequirements", icon: FaShieldAlt },
  { key: "documents", icon: FaFileAlt },
  { key: "cover", icon: FaFileSignature },
  { key: "review", icon: FaPaperPlane },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType }>;

export type BidStep = (typeof BID_WIZARD_STEPS)[number]["key"];

// Whether each conditionally-shown step has content to answer. Derived from the event with
// bidStepVisibility(); threaded through the wizard so nav, neighbors and the URL guard agree.
export interface BidStepVisibility {
  collectSupplierPrice: boolean;
  // Server-derived: false when the supplier declared it where they were admitted — with the
  // participation request, or at admission to the system — so the tender wizard drops those steps.
  espdTravelsWithBid: boolean;
  asksForEspd: boolean;
  hasExclusionCriteria: boolean;
  hasSelectionCriteria: boolean;
  hasMinimumRequirements: boolean;
  hasAwardCriteria: boolean;
}

export const bidStepVisibility = (event: PortalSourcingEventDetailDto): BidStepVisibility => ({
  collectSupplierPrice: event.collectSupplierPrice ?? true,
  espdTravelsWithBid: event.collectsEspdWithBid ?? true,
  asksForEspd: event.asksForEspd ?? true,
  hasExclusionCriteria: (event.applicableExclusionCriteria ?? []).length > 0,
  hasSelectionCriteria: (event.selectionCriteria ?? []).length > 0,
  hasMinimumRequirements: (event.minimumRequirements ?? []).length > 0,
  hasAwardCriteria: (event.awardCriteria?.criteria ?? []).length > 0,
});

const isStepVisible = (key: BidStep, v: BidStepVisibility): boolean => {
  switch (key) {
    // The tendered price is answered inside this step, so it stays whenever either is collected.
    case "awardCriteria":
      return v.collectSupplierPrice || v.hasAwardCriteria;
    case "supplier":
      return v.espdTravelsWithBid && v.asksForEspd;
    case "exclusion":
      return v.espdTravelsWithBid && v.hasExclusionCriteria;
    case "qualification":
      return v.espdTravelsWithBid && v.hasSelectionCriteria;
    case "minimumRequirements":
      return v.hasMinimumRequirements;
    default:
      return true;
  }
};

export const bidWizardModel = defineWizardSteps<BidStep, BidStepVisibility>(BID_WIZARD_STEPS, isStepVisible);
